/**
 * useAvatarBridge
 *
 * React hook that installs bridge handlers on AvatarBridge and wires them
 * to the platform-shell's thread store + programmatically-sendable chat
 * message API. Mount from GlobalChatSystem.tsx (or equivalent host) once
 * the thread store and chat are ready; unmount cleans up automatically.
 *
 * Contract (see AVATAR_BRIDGE_PROTOCOL.md on this repo root):
 *   - Handlers receive parsed params, return a result object or throw.
 *   - Per-account scope is the CALLER's responsibility: callers pass the
 *     iframe + origin already scoped to the current user's avatar VM.
 *     This hook does NOT re-check origin (AvatarBridge does that).
 *
 * Events broadcast:
 *   - thread.created      (when newThread succeeds)
 *   - thread.switched     (when switchThread succeeds)
 *
 * Workflow events (workflow.started / clarifying-question / ...) are
 * broadcast from elsewhere by calling AvatarBridge.broadcastEvent();
 * this hook only handles request dispatch.
 *
 * Per-account scope hardening:
 *   - The hook does NOT accept cross-account threadIds. The platform
 *     shell only ever has one user's threads at a time (via
 *     useChatThreads), so threadId lookup is implicitly scoped.
 *   - A malicious iframe could try to submit messages to its own (scoped)
 *     thread; the bridge allows this because that's what the feature is.
 */

import { useEffect, useRef } from 'react';
import AvatarBridge, {
  type BridgeMethod,
  type BridgeEventName,
} from '../services/avatarBridge/AvatarBridge';
import { readBufferedEvents, type BufferedBridgeEvent } from '../services/agenticWorkflows/broadcastBridgeEvent';

type BridgeHandler = Parameters<typeof AvatarBridge.registerHandlers>[0];

/**
 * Minimal shape the bridge needs from the thread store. Kept narrow so
 * the hook is unit-testable without the full useChatThreads surface.
 */
export interface BridgeThreadStore {
  newThread: () => string;
  switchThread: (threadId: string) => void;
  renameThread: (threadId: string, title: string) => void;
  threads: ReadonlyArray<{
    id: string;
    title?: string;
    unread?: number;
    lastActivityAt?: number;
    status?: string;
  }>;
  activeThreadId: string | null;
}

/**
 * Minimal shape the bridge needs from the chat system.
 * sendProgrammaticMessage is the documented public API for external submits
 * (per useGlobalChat.ts's return type).
 */
export interface BridgeChatSystem {
  sendProgrammaticMessage: (
    text: string,
    options?: { hidden?: boolean }
  ) => Promise<void>;
}

export interface UseAvatarBridgeArgs {
  /** Thread store. */
  threadStore: BridgeThreadStore;
  /** Chat system. */
  chat: BridgeChatSystem;
  /**
   * Label for debug logs (e.g. account id or user email). Optional.
   */
  label?: string;
  /**
   * Whether to install handlers. False disables the bridge temporarily
   * (e.g. during thread-store hydration). Defaults to true.
   */
  enabled?: boolean;
}


function findThread(store: BridgeThreadStore, id: string) {
  return store.threads.find((t) => t.id === id) || null;
}

/**
 * Build the handler registry from the provided thread store and chat.
 *
 * Split out from the hook so it's importable + testable outside React.
 */
export function buildBridgeHandlers(opts: {
  threadStore: BridgeThreadStore;
  chat: BridgeChatSystem;
  broadcastEvent: (e: { event: BridgeEventName; threadId: string; payload?: Record<string, unknown> }) => void;
  /**
   * L0.4 (2026-09-08): when given, every handler reads the thread store through this getter at
   * CALL time, so the hook can build the handlers once and keep their state (the duplicate-thread
   * guard's `lastOpenedThreadId`) across thread switches. Without it (the tests' shape) the
   * snapshot passed as `threadStore` is used.
   */
  getThreadStore?: () => BridgeThreadStore;
}): BridgeHandler {
  const { chat, broadcastEvent } = opts;
  const store = (): BridgeThreadStore => (opts.getThreadStore ? opts.getThreadStore() : opts.threadStore);
  // 2026-05-23: module-level record updated by every newThread / submitToThread
  // call. Used by event-emission code (via AvatarBridge.getActiveThreadId)
  // as a fallback so workflow.started / thread.clarifying-question /
  // workflow.completed events always resolve to a real thread id — even
  // in the window between newThread() returning and the React effect
  // re-registering the live getter.
  let lastHandledThreadId: string | null = null;

  // 2026-05-28 (Justin: duplicate empty thread bug). Track the most recently
  // OPENED thread so submit_to_thread can reuse it when the avatar passes a
  // bad/placeholder threadId. The dominant real-world pattern is
  // open_thread_for_workflow immediately followed by submit_to_thread with a
  // placeholder id (e.g. "placeholder-thread-id", "<dynamic-thread-id>"). Before
  // this guard, the unknown id caused a SECOND fresh thread to be spawned,
  // leaving an empty nicely-titled stub thread plus an ugly truncated-prompt
  // thread in the user's taskbar. Reusing the just-created thread keeps it to
  // one correctly-titled thread.
  let lastOpenedThreadId: string | null = null;
  let lastOpenedAtMs = 0;
  const RECENT_OPEN_WINDOW_MS = 30_000;

  const primeActiveGetter = (id: string) => {
    lastHandledThreadId = id;
    AvatarBridge.registerActiveThreadIdGetter(() => lastHandledThreadId);
  };

  return {
    newThread: async (params) => {
      const title = typeof params.title === 'string' ? params.title : undefined;
      const id = store().newThread();
      lastOpenedThreadId = id;
      lastOpenedAtMs = Date.now();
      primeActiveGetter(id);
      if (title) {
        try { store().renameThread(id, title); } catch { /* rename is best-effort */ }
      }
      broadcastEvent({ event: 'thread.created', threadId: id, payload: { title } });
      return { threadId: id, title, createdAt: new Date().toISOString() };
    },
    switchThread: async (params) => {
      const threadId = typeof params.threadId === 'string' ? params.threadId : '';
      if (!threadId) throw new Error('parameter threadId: string is required');
      let id = threadId;
      if (!findThread(store(), threadId)) {
        // 2026-05-23 auto-recovery: if the requested id isn't in the thread
        // store (closed thread / React race / stale id from a prior
        // session), create a fresh thread under the same title so the
        // avatar's flow doesn't crash with thread-not-found.
        // eslint-disable-next-line no-console
        console.warn('[avatar-bridge] switch_thread: id', threadId, 'not in store — creating fresh thread');
        id = store().newThread();
      }
      primeActiveGetter(id);
      store().switchThread(id);
      broadcastEvent({ event: 'thread.switched', threadId: id, payload: {} });
      return { threadId: id };
    },
    // 2026-05-23: submitToThread auto-recovers when the avatar passes a
    // stale threadId. Instead of throwing thread-not-found (which caused
    // the avatar to fall back to prest0n_brain and run work invisibly
    // in the background), we create a fresh thread, rename it from the
    // submission text, switch to it, and proceed. Returns success in
    // <1s so the bridge call completes inside the timeout regardless of
    // workflow execution latency.
    submitToThread: async (params) => {
      const threadId = typeof params.threadId === 'string' ? params.threadId : '';
      const text = typeof params.text === 'string' ? params.text : '';
      if (!threadId) throw new Error('parameter threadId: string is required');
      if (!text) throw new Error('parameter text: string is required');

      let effectiveThreadId = threadId;
      if (!findThread(store(), threadId)) {
        // 2026-05-28 duplicate-thread guard: the passed id is unknown (almost
        // always a placeholder the avatar failed to substitute, e.g.
        // "placeholder-thread-id" / "<dynamic-thread-id>"). If we just opened a
        // thread moments ago, REUSE it instead of spawning a duplicate.
        const recentlyOpened =
          lastOpenedThreadId &&
          findThread(store(), lastOpenedThreadId) &&
          Date.now() - lastOpenedAtMs <= RECENT_OPEN_WINDOW_MS;
        if (recentlyOpened) {
          // eslint-disable-next-line no-console
          console.warn('[avatar-bridge] submit_to_thread: id', threadId, 'not in store — reusing recently-opened thread', lastOpenedThreadId, 'instead of spawning a duplicate');
          effectiveThreadId = lastOpenedThreadId as string;
          if (store().activeThreadId !== effectiveThreadId) {
            store().switchThread(effectiveThreadId);
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('[avatar-bridge] submit_to_thread: id', threadId, 'not in store and no recent open — creating fresh thread and auto-recovering');
          const freshId = store().newThread();
          try { store().renameThread(freshId, text.slice(0, 60)); } catch {}
          effectiveThreadId = freshId;
        }
      } else if (store().activeThreadId !== threadId) {
        store().switchThread(threadId);
      }

      primeActiveGetter(effectiveThreadId);

      Promise.resolve()
        .then(() => chat.sendProgrammaticMessage(text, { hidden: false }))
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error('[avatar-bridge] submit_to_thread dispatch failed:', e);
        });

      return {
        threadId: effectiveThreadId,
        messageId: `msg-${Date.now().toString(36)}`,
        status: 'dispatched',
      };
    },
    listThreads: async () => ({
      threads: store().threads.slice(0, 50).map((t) => ({
        id: t.id,
        title: t.title || '',
        unread: t.unread || 0,
        lastActivityAt: t.lastActivityAt || 0,
        status: t.status || 'active',
      })),
    }),
    getActiveThread: async () => {
      const id = store().activeThreadId;
      const t = id ? findThread(store(), id) : null;
      return {
        threadId: id === null ? null : id,
        title: t?.title || null,
      };
    },
    // 2026-05-23: get_workflow_events — avatar polls this to fetch recent
    // workflow events from a specific thread. Returns only events after
    // `sinceTimestamp` (ISO string) so repeated polls don't re-deliver
    // the same events. This is the reliable fallback / primary channel
    // for workflow narration. The push channel (postMessage) is
    // best-effort; the poll channel is always-available and always-accurate
    // because it reads from the server-side event buffer the workflow
    // handlers append to.
    getWorkflowEvents: async (params) => {
      const threadIds = Array.isArray(params.threadIds) ? params.threadIds.filter((x): x is string => typeof x === 'string') : [];
      const sinceTimestamp = typeof params.sinceTimestamp === 'string' ? params.sinceTimestamp : null;
      const sinceMs = sinceTimestamp ? Date.parse(sinceTimestamp) : undefined;
      const events: BufferedBridgeEvent[] = readBufferedEvents({ threadIds: threadIds.length > 0 ? threadIds : undefined, sinceMs: Number.isFinite(sinceMs) ? sinceMs : undefined });
      return {
        events,
        watermark: new Date().toISOString(),
      };
    },
  };
}

/**
 * useAvatarBridge — wires platform-shell thread store + chat into the
 * AvatarBridge singleton. Called once from the component that owns those
 * two pieces (typically GlobalChatSystem, one-per-app).
 *
 * Iframe registration + origin allowlisting done separately at the avatar
 * iframe mount site via AvatarBridge.registerIframe / setAllowedOrigins.
 */
export function useAvatarBridge(args: UseAvatarBridgeArgs): void {
  const { threadStore, chat, label, enabled = true } = args;
  const storeRef = useRef(threadStore);
  const chatRef = useRef(chat);
  storeRef.current = threadStore;
  chatRef.current = chat;

  useEffect(() => {
    if (!enabled) return;
    // L0.4 (2026-09-08): built ONCE per mount. The handlers read the live store through the ref
    // getter, so a thread switch no longer rebuilds them — the duplicate-thread guard's
    // "recently opened" memory used to be wiped on every switch (the effect depended on
    // activeThreadId), which is exactly when the avatar's placeholder-id submit arrives.
    const handlers = buildBridgeHandlers({
      threadStore: storeRef.current,
      getThreadStore: () => storeRef.current,
      chat: chatRef.current,
      broadcastEvent: (e) => AvatarBridge.broadcastEvent(e),
    });
    AvatarBridge.registerHandlers(handlers);
    // Register active-thread-id getter so workflow handlers (outside React)
    // can read the current thread id when broadcasting events.
    AvatarBridge.registerActiveThreadIdGetter(() => storeRef.current.activeThreadId);
    // eslint-disable-next-line no-console
    console.log('[useAvatarBridge] handlers registered', label || '(no label)');
    return () => {
      AvatarBridge.clearHandlers([
        'newThread',
        'switchThread',
        'submitToThread',
        'listThreads',
        'getActiveThread',
        'getWorkflowEvents',
      ] as BridgeMethod[]);
      AvatarBridge.registerActiveThreadIdGetter(() => null);
    };
  }, [enabled, label]);
}

export default useAvatarBridge;
